import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  ClipboardList,
  Clock,
  CheckCircle,
  RefreshCw,
} from 'lucide-react';
import { complianceTasksAPI, dashboardAPI, produceTrackerAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { summarizeDailyCompliance } from '../../utils/complianceSummary';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import FilterSelect from '../../components/common/FilterSelect';
import StoreCloseControl from './StoreCloseControl';
import FinalReport from '../Reports/FinalReport';

const DAILY_TASK_QUOTA = 5;
const TASK_TYPE_OPTIONS = [
  { value: 'all', label: 'All Tasks' },
  { value: 'dateCheck', label: 'Date Check' },
  { value: 'shortlifeDateCheck', label: 'Short-life' },
  { value: 'produceDateCheck', label: 'Produce' },
  { value: 'dailyInventory', label: 'Inventory' },
  { value: 'temperatureCheck', label: 'Temperature' },
];

const formatDuration = (minutesValue) => {
  if (!minutesValue || minutesValue <= 0) return '0m';
  const totalMinutes = Math.round(minutesValue);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

const formatLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatUpcomingDateTime = (value) => {
  if (!value) return 'No due time';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'No due time';
  return parsed.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const normalizeTaskStatus = (value) => {
  const status = String(value || 'pending').toLowerCase();
  if (status === 'completed') return 'completed';
  if (status === 'in-progress') return 'in-progress';
  if (status === 'overdue') return 'overdue';
  return 'pending';
};

const getStatusBadgeClass = (status) => {
  if (status === 'completed') return 'badge-success';
  if (status === 'in-progress') return 'badge-warning';
  if (status === 'overdue') return 'badge-danger';
  return 'badge-info';
};

const getStatusLabel = (status) => {
  if (status === 'completed') return 'Completed';
  if (status === 'in-progress') return 'In Progress';
  if (status === 'overdue') return 'Overdue';
  return 'Pending';
};

const getPriorityBadgeClass = (priorityValue) => {
  const priority = String(priorityValue || '').toLowerCase();
  if (priority === 'urgent') return 'badge-danger';
  if (priority === 'high') return 'badge-warning';
  return 'badge-info';
};

const shouldShowStatusBadge = (statusValue) => normalizeTaskStatus(statusValue) !== 'pending';
const shouldShowPriorityBadge = (priorityValue) => String(priorityValue || '').toLowerCase() !== 'medium';

const shiftDate = (dateKey, days) => {
  const [year, month, day] = String(dateKey).split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  date.setDate(date.getDate() + days);
  return formatLocalDateKey(date);
};

const parseSlotMinutes = (slotValue = '') => {
  const match = String(slotValue).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return Number.NaN;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.NaN;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return Number.NaN;
  return (hours * 60) + minutes;
};

const extractTaskDateKey = (task) => {
  const rawValue = task?.scheduled_date || task?.dueDate || task?.due_date || task?.completed_at || '';
  if (!rawValue) return '';
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatLocalDateKey(parsed);
};

const extractTaskSlotKey = (task) => {
  const slotValue = String(task?.slotTime || '').trim();
  if (slotValue) {
    const minutes = parseSlotMinutes(slotValue);
    if (Number.isFinite(minutes)) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    }
    return slotValue;
  }

  const rawValue = task?.scheduled_date || task?.dueDate || task?.due_date || '';
  if (!rawValue) return '';
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
};

const getTaskRank = (task) => {
  const idValue = String(task?.id || task?._id || '');
  const suffix = idValue.match(/-(\d+)$/);
  if (suffix) return Number(suffix[1]) || 0;
  const numeric = Number(idValue);
  return Number.isFinite(numeric) ? numeric : 0;
};

const dedupeUpcomingTasks = (tasks = []) => {
  const latestRosterPerHour = new Map();
  const nonRoster = [];

  tasks.forEach((task) => {
    if (String(task?.kind || '') !== 'workforce_hourly') {
      nonRoster.push(task);
      return;
    }

    const dateKey = extractTaskDateKey(task);
    const slotKey = extractTaskSlotKey(task);
    if (!dateKey || !slotKey) {
      nonRoster.push(task);
      return;
    }

    const bucketKey = `${dateKey}::${slotKey}`;
    const current = latestRosterPerHour.get(bucketKey);
    if (!current) {
      latestRosterPerHour.set(bucketKey, task);
      return;
    }

    if (getTaskRank(task) >= getTaskRank(current)) {
      latestRosterPerHour.set(bucketKey, task);
    }
  });

  return [...nonRoster, ...Array.from(latestRosterPerHour.values())];
};

const getDateRange = (startDateKey, days) =>
  Array.from({ length: days }, (_, index) => shiftDate(startDateKey, index));

const groupLogsByStore = (logs = []) =>
  logs.reduce((acc, log) => {
    const storeId = log.store_id || 'unknown-store';
    if (!acc[storeId]) {
      acc[storeId] = [];
    }
    acc[storeId].push(log);
    return acc;
  }, {});

const getCompletedTaskCount = (logs = [], selectedTaskType = 'all', aggregateByStore = false) => {
  if (!aggregateByStore) {
    const summary = summarizeDailyCompliance(logs);
    if (selectedTaskType === 'all') {
      return summary.completedTaskCount;
    }
    return summary.tasks?.[selectedTaskType]?.completed ? 1 : 0;
  }

  return Object.values(groupLogsByStore(logs)).reduce((sum, storeLogs) => (
    sum + getCompletedTaskCount(storeLogs, selectedTaskType, false)
  ), 0);
};

const getTaskDayDurationMinutes = (logs = []) => {
  const grouped = logs.reduce((acc, log) => {
    if (Number(log.subtask_completed || 0) <= 0) return acc;
    const key = `${log.store_id || 'store'}::${log.task_type || 'task'}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(log);
    return acc;
  }, {});

  return Object.values(grouped).reduce((sum, taskLogs) => {
    const timestamps = taskLogs
      .map((log) => log.completed_at || log.updated_at || log.created_at)
      .filter(Boolean)
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value));

    if (timestamps.length === 0) return sum;
    const start = Math.min(...timestamps);
    const end = Math.max(...timestamps);
    return sum + Math.max(1, (end - start) / (1000 * 60));
  }, 0);
};

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="card">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
      </div>
      <div className={`rounded-lg p-3 ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </div>
);

const CompletionRateCard = ({ value }) => {
  const percentage = Math.max(0, Math.min(100, Math.round(value || 0)));
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-600">Completion Rate</p>
          <p className="mt-1 text-xs text-gray-500">Based on today&apos;s assigned tasks</p>
        </div>
        <div className="relative h-24 w-24">
          <svg className="h-24 w-24 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={radius} stroke="#e5e7eb" strokeWidth="10" fill="none" />
            <circle
              cx="50"
              cy="50"
              r={radius}
              stroke="#22c55e"
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-gray-900">
            {percentage}%
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, isManager, isSupervisor } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const queryClient = useQueryClient();
  const isSupervisorUser = isSupervisor();
  const isPrivilegedUser = isManager();
  const showExecutiveFinalReportDashboard = isPrivilegedUser;
  const isOperationalLead = isPrivilegedUser || isSupervisorUser;
  const showDailyTaskTimeSummary = isOperationalLead;
  const showUpcomingTasks = !isOperationalLead;
  const hasToken = !!localStorage.getItem('token');
  const selectedStoreId = selectedStore?.storeInfoId || selectedStore?.store_info_id || selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '';
  const selectedVendorId = selectedStore?.vendorId || selectedStore?.vendor_id || '';
  const selectedStoreName = selectedStore?.name || selectedStore?.storeName || selectedStore?.store_code || selectedStore?.storeCode || 'Selected Store';
  const todayKey = formatLocalDateKey();
  const tomorrowKey = shiftDate(todayKey, 1);
  const performanceDays = 7;
  const performanceStartKey = shiftDate(todayKey, -performanceDays + 1);
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');
  const [executiveDashboardFilters, setExecutiveDashboardFilters] = useState({
    date: todayKey,
    storeId: selectedStoreId || '',
    selectedStoreName,
  });

  useEffect(() => {
    setExecutiveDashboardFilters((current) => ({
      ...current,
      storeId: selectedStoreId || '',
      selectedStoreName: selectedStoreId ? selectedStoreName : 'Select store from header',
    }));
  }, [selectedStoreId, selectedStoreName]);

  const { data: dashboardStatsData, refetch, isLoading } = useQuery('dashboardStats', dashboardAPI.getStats, {
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 60000,
    enabled: hasToken && !showExecutiveFinalReportDashboard,
    staleTime: 30 * 1000,
    cacheTime: 30 * 60 * 1000,
    keepPreviousData: true,
  });

  const { data: upcomingTasksData } = useQuery('upcomingTasks', dashboardAPI.getUpcomingTasks, {
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    enabled: hasToken && !showExecutiveFinalReportDashboard && showUpcomingTasks,
    staleTime: 2 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  const { data: complianceLogsData } = useQuery(
    [
      'dashboardComplianceLogs',
      showExecutiveFinalReportDashboard ? executiveDashboardFilters.date : performanceStartKey,
      showExecutiveFinalReportDashboard ? executiveDashboardFilters.date : todayKey,
      showExecutiveFinalReportDashboard ? executiveDashboardFilters.storeId : selectedStoreId,
      isPrivilegedUser,
      showExecutiveFinalReportDashboard,
    ],
    () => complianceTasksAPI.getLogs({
      dateFrom: showExecutiveFinalReportDashboard ? executiveDashboardFilters.date : performanceStartKey,
      dateTo: showExecutiveFinalReportDashboard ? executiveDashboardFilters.date : todayKey,
      ...(
        showExecutiveFinalReportDashboard
          ? (executiveDashboardFilters.storeId ? { storeId: executiveDashboardFilters.storeId } : {})
          : (selectedStoreId ? { storeId: selectedStoreId } : {})
      ),
    }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: 60000,
      enabled: hasToken && Boolean(showExecutiveFinalReportDashboard ? executiveDashboardFilters.storeId : selectedStoreId),
      staleTime: 30 * 1000,
      cacheTime: 30 * 60 * 1000,
      keepPreviousData: true,
    }
  );

  const isSupervisorReminderUser = showUpcomingTasks && ['Supervisor'].includes(user?.role);
  const { data: produceTrackerTodayData } = useQuery(
    ['dashboardProduceTrackerToday', selectedStoreId, todayKey],
    () => produceTrackerAPI.getAll({
      storeId: selectedStoreId,
      fromDate: todayKey,
      toDate: todayKey,
      limit: 5,
    }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      enabled: hasToken && !showExecutiveFinalReportDashboard && isSupervisorReminderUser && Boolean(selectedStoreId),
      staleTime: 2 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const upcomingTasks = useMemo(() => upcomingTasksData?.data?.data || [], [upcomingTasksData]);
  const complianceLogs = useMemo(() => complianceLogsData?.data?.data || [], [complianceLogsData]);
  const hasTodayProduceRecord = useMemo(() => {
    const entries = produceTrackerTodayData?.data?.data || [];
    return entries.some((record) => (
      String(record?.storeId || '') === String(selectedStoreId)
      && String(record?.date || '') === String(todayKey)
    ));
  }, [produceTrackerTodayData, selectedStoreId, todayKey]);

  const accessibleStores = useMemo(() => {
    if (!selectedStoreId) {
      return [];
    }
    return [{ id: selectedStoreId, name: selectedStoreName }];
  }, [selectedStoreId, selectedStoreName]);

  const performanceDates = useMemo(
    () => getDateRange(performanceStartKey, performanceDays),
    [performanceDays, performanceStartKey]
  );

  const performance = useMemo(
    () => performanceDates.map((dateKey) => {
      const dayLogs = complianceLogs.filter((log) => log.task_date === dateKey);
      const completed = getCompletedTaskCount(dayLogs, 'all', isPrivilegedUser);
      return {
        date: dateKey,
        completed,
        assigned: accessibleStores.length * DAILY_TASK_QUOTA,
      };
    }),
    [accessibleStores.length, complianceLogs, isPrivilegedUser, performanceDates]
  );

  const todayPoint = performance[performance.length - 1] || {
    assigned: accessibleStores.length * DAILY_TASK_QUOTA,
    completed: 0,
  };
  const dashboardTaskStats = dashboardStatsData?.data?.data?.tasks || {};
  const executiveStoreCount = useMemo(() => {
    if (!showExecutiveFinalReportDashboard) {
      return accessibleStores.length;
    }
    return executiveDashboardFilters.storeId ? 1 : 0;
  }, [
    accessibleStores.length,
    executiveDashboardFilters.storeId,
    showExecutiveFinalReportDashboard,
  ]);
  const executiveAggregateByStore = false;
  const todayTasks = showExecutiveFinalReportDashboard
    ? executiveStoreCount * DAILY_TASK_QUOTA
    : (Number(dashboardTaskStats.today) || Number(todayPoint.assigned) || 0);
  const completedToday = showExecutiveFinalReportDashboard
    ? getCompletedTaskCount(complianceLogs, 'all', executiveAggregateByStore)
    : (Number(dashboardTaskStats.completedToday) || Number(todayPoint.completed) || 0);
  const pendingToday = showExecutiveFinalReportDashboard
    ? Math.max(0, todayTasks - completedToday)
    : Math.max(0, Number(dashboardTaskStats.myPending) || (todayTasks - completedToday));
  const completionRate = todayTasks > 0 ? (completedToday / todayTasks) * 100 : 0;

  const productivityRows = useMemo(
    () => (
      showDailyTaskTimeSummary
        ? performanceDates.map((dateKey) => {
            const dayLogs = complianceLogs.filter((log) => log.task_date === dateKey);
            const relevantLogs = taskTypeFilter === 'all'
              ? dayLogs
              : dayLogs.filter((log) => log.task_type === taskTypeFilter);
            const staffCount = new Set(relevantLogs.map((log) => log.user_id).filter(Boolean)).size;
            const completedTaskCount = getCompletedTaskCount(dayLogs, taskTypeFilter, isPrivilegedUser);
            const totalMinutes = getTaskDayDurationMinutes(relevantLogs);
            return {
              date: dateKey,
              staffCount,
              completedTaskCount,
              totalMinutes,
              avgMinutesPerCompletedTask: completedTaskCount > 0 ? totalMinutes / completedTaskCount : 0,
              avgMinutesPerStaff: staffCount > 0 ? totalMinutes / staffCount : 0,
            };
          })
        : []
    ),
    [complianceLogs, isPrivilegedUser, performanceDates, showDailyTaskTimeSummary, taskTypeFilter]
  );

  const productivityTotals = productivityRows.reduce(
    (acc, row) => ({
      staffMax: Math.max(acc.staffMax, row.staffCount),
      completedTaskSum: acc.completedTaskSum + row.completedTaskCount,
      totalMinutesSum: acc.totalMinutesSum + row.totalMinutes,
    }),
    { staffMax: 0, completedTaskSum: 0, totalMinutesSum: 0 }
  );

  const productivityAverages = {
    staffAvg: productivityRows.length > 0
      ? productivityRows.reduce((sum, row) => sum + row.staffCount, 0) / productivityRows.length
      : 0,
    completedTaskAvg: productivityRows.length > 0
      ? productivityTotals.completedTaskSum / productivityRows.length
      : 0,
    totalMinutesAvg: productivityRows.length > 0
      ? productivityTotals.totalMinutesSum / productivityRows.length
      : 0,
  };

  const produceReminderTasks = useMemo(() => {
    if (!isSupervisorReminderUser || !selectedStoreId) {
      return [];
    }

    const now = new Date();
    const currentHour = now.getHours();
    const todayDateKey = formatLocalDateKey(now);

    const reminders = [];

    if (currentHour < 11 && !hasTodayProduceRecord) {
      reminders.push({
        id: `produce-opening-${todayDateKey}`,
        taskId: { name: 'Opening Inventory Pending' },
        dueDate: `${todayDateKey}T11:00:00`,
        priority: 'high',
        status: 'pending',
        details: 'Complete opening inventory in Produce Tracker before 11:00 AM.',
      });
    }

    if (currentHour >= 9 && currentHour < 18 && !hasTodayProduceRecord) {
      reminders.push({
        id: `produce-wastage-${todayDateKey}`,
        taskId: { name: 'Wastage Entry Pending' },
        dueDate: `${todayDateKey}T18:00:00`,
        priority: 'urgent',
        status: 'pending',
        details: 'Complete wastage update in Produce Tracker before 6:00 PM.',
      });
    }

    return reminders;
  }, [hasTodayProduceRecord, isSupervisorReminderUser, selectedStoreId]);

  const mergedUpcomingTasks = useMemo(
    () => (
      showUpcomingTasks
        ? dedupeUpcomingTasks([...produceReminderTasks, ...upcomingTasks])
          .sort((left, right) => new Date(left.scheduled_date || left.dueDate || left.due_date || 0).getTime() - new Date(right.scheduled_date || right.dueDate || right.due_date || 0).getTime())
        : []
    ),
    [produceReminderTasks, showUpcomingTasks, upcomingTasks]
  );

  const todayUpcomingTasks = useMemo(
    () => mergedUpcomingTasks.filter((task) => {
      const value = task.scheduled_date || task.dueDate || task.due_date || task.completed_at;
      if (!value) return false;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return false;
      return formatLocalDateKey(parsed) === todayKey;
    }),
    [mergedUpcomingTasks, todayKey]
  );

  const tomorrowUpcomingTasks = useMemo(
    () => mergedUpcomingTasks.filter((task) => {
      const value = task.scheduled_date || task.dueDate || task.due_date || task.completed_at;
      if (!value) return false;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return false;
      return formatLocalDateKey(parsed) === tomorrowKey;
    }),
    [mergedUpcomingTasks, tomorrowKey]
  );

  if (showExecutiveFinalReportDashboard) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-1 text-gray-500">Welcome back, {user?.name}</p>
          </div>
          <StoreCloseControl
            storeInfoId={selectedStoreId}
            vendorId={selectedVendorId}
            storeName={selectedStoreName}
            todayKey={todayKey}
          />
        </div>

        <div>
          <h2 className="text-[24px] font-bold tracking-tight text-gray-900">Task Performance</h2>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          <StatCard
            title="Assigned Tasks"
            value={todayTasks}
            icon={Clock}
            color="bg-warning-500"
            subtitle="Tasks scheduled today"
          />
          <StatCard
            title="Completed Today"
            value={completedToday}
            icon={CheckCircle}
            color="bg-success-500"
            subtitle="Tasks finished today"
          />
          <StatCard
            title="Pending Tasks"
            value={pendingToday}
            icon={ClipboardList}
            color="bg-primary-500"
            subtitle={`${todayTasks} assigned - ${completedToday} completed`}
          />
          <CompletionRateCard value={completionRate} />
        </div>

        <FinalReport
          headerTitle="Store Performance"
          headerDescription=""
          onFilterStateChange={setExecutiveDashboardFilters}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-gray-500">Welcome back, {user?.name}</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <StoreCloseControl
            storeInfoId={selectedStoreId}
            vendorId={selectedVendorId}
            storeName={selectedStoreName}
            todayKey={todayKey}
          />
          <button
            onClick={async () => {
              await queryClient.invalidateQueries([
                'storeCloseStatus',
                String(selectedStoreId || ''),
                String(selectedVendorId || ''),
                todayKey,
              ]);
              await queryClient.invalidateQueries('dashboardStats');
              await queryClient.invalidateQueries('dashboardComplianceLogs');
              await queryClient.invalidateQueries('dashboardProduceTrackerToday');
              await queryClient.invalidateQueries('upcomingTasks');
              await refetch();
            }}
            className="flex items-center space-x-2 rounded-lg bg-primary-500 px-4 py-2 text-white transition-colors hover:bg-primary-600 disabled:opacity-50"
            disabled={isLoading}
            title="Refresh dashboard data"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>{isLoading ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>
        <FinalReport
          headerTitle="Store Performance"
          headerDescription=""
          onFilterStateChange={setExecutiveDashboardFilters}
        />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <StatCard
          title="Assigned Tasks"
          value={todayTasks}
          icon={Clock}
          color="bg-warning-500"
          subtitle="Tasks scheduled today"
        />
        <StatCard
          title="Completed Today"
          value={completedToday}
          icon={CheckCircle}
          color="bg-success-500"
          subtitle="Tasks finished today"
        />
        <StatCard
          title="Pending Tasks"
          value={pendingToday}
          icon={ClipboardList}
          color="bg-primary-500"
          subtitle={`${todayTasks} assigned - ${completedToday} completed`}
        />
        <CompletionRateCard value={completionRate} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        <div className="card self-start">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Performance (Last 7 Days)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="completed" stroke="#22c55e" name="Completed" />
                <Line type="monotone" dataKey="assigned" stroke="#3b82f6" name="Assigned" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card self-start">
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {showDailyTaskTimeSummary ? 'Daily Task Time Summary' : 'Upcoming Tasks'}
            </h3>
            {showDailyTaskTimeSummary && (
              <FilterSelect
                value={taskTypeFilter}
                onChange={setTaskTypeFilter}
                options={TASK_TYPE_OPTIONS}
                className="min-w-[180px]"
              />
            )}
          </div>

          <div className="space-y-5 overflow-visible pr-1">
            {showDailyTaskTimeSummary ? (
              <>
                <div className="space-y-3 lg:hidden">
                  {productivityRows.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-6 text-center text-gray-500">
                      No task activity found for the last 7 days
                    </div>
                  ) : (
                    productivityRows.map((row) => (
                      <div key={row.date} className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="mb-2 text-sm font-semibold text-gray-900">
                          {new Date(`${row.date}T00:00:00`).toLocaleDateString()}
                        </p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                          <div>
                            <p className="text-gray-500">Staff</p>
                            <p className="font-medium text-gray-800">{row.staffCount}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Completed</p>
                            <p className="font-medium text-gray-800">{row.completedTaskCount}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Total Time</p>
                            <p className="font-medium text-gray-800">{formatDuration(row.totalMinutes)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Avg / Task</p>
                            <p className="font-medium text-gray-800">{formatDuration(row.avgMinutesPerCompletedTask)}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-500">Avg / Staff</p>
                            <p className="font-medium text-gray-800">{formatDuration(row.avgMinutesPerStaff)}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  {productivityRows.length > 0 ? (
                    <div className="rounded-lg border border-gray-300 bg-gray-50 p-3">
                      <p className="mb-2 text-sm font-semibold text-gray-900">Totals</p>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                        <div>
                          <p className="text-gray-500">Max Staff</p>
                          <p className="font-semibold text-gray-900">{productivityTotals.staffMax}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Completed Sum</p>
                          <p className="font-semibold text-gray-900">{productivityTotals.completedTaskSum}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total Time</p>
                          <p className="font-semibold text-gray-900">{formatDuration(productivityTotals.totalMinutesSum)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Grand Avg Time</p>
                          <p className="font-semibold text-gray-900">{formatDuration(productivityAverages.totalMinutesAvg)}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="hidden lg:block">
                  <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-500">
                      <th className="whitespace-nowrap px-3 py-2 font-medium">Date</th>
                      <th className="whitespace-nowrap px-3 py-2 font-medium">Staff</th>
                      <th className="whitespace-nowrap px-3 py-2 font-medium">Completed Tasks</th>
                      <th className="whitespace-nowrap px-3 py-2 font-medium">Total Time</th>
                      <th className="whitespace-nowrap px-3 py-2 font-medium">Avg / Task</th>
                      <th className="whitespace-nowrap px-3 py-2 font-medium">Avg / Staff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productivityRows.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-3 py-6 text-center text-gray-500">
                          No task activity found for the last 7 days
                        </td>
                      </tr>
                    ) : (
                      productivityRows.map((row) => (
                        <tr key={row.date} className="border-b border-gray-100 last:border-b-0">
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {new Date(`${row.date}T00:00:00`).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2 text-gray-700">{row.staffCount}</td>
                          <td className="px-3 py-2 text-gray-700">{row.completedTaskCount}</td>
                          <td className="px-3 py-2 text-gray-700">{formatDuration(row.totalMinutes)}</td>
                          <td className="px-3 py-2 text-gray-700">
                            {formatDuration(row.avgMinutesPerCompletedTask)}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {formatDuration(row.avgMinutesPerStaff)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {productivityRows.length > 0 && (
                    <tfoot>
                      <tr className="border-t border-gray-300 bg-gray-50 font-semibold text-gray-900">
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2">{productivityTotals.staffMax}</td>
                        <td className="px-3 py-2">{productivityTotals.completedTaskSum}</td>
                        <td className="px-3 py-2">{formatDuration(productivityTotals.totalMinutesSum)}</td>
                        <td className="px-3 py-2">-</td>
                        <td className="px-3 py-2">-</td>
                      </tr>
                      <tr className="bg-gray-50 font-semibold text-gray-900">
                        <td className="px-3 py-2">Grand Avg</td>
                        <td className="px-3 py-2">{productivityAverages.staffAvg.toFixed(1)}</td>
                        <td className="px-3 py-2">{productivityAverages.completedTaskAvg.toFixed(1)}</td>
                        <td className="px-3 py-2">{formatDuration(productivityAverages.totalMinutesAvg)}</td>
                        <td className="px-3 py-2">
                          {formatDuration(
                            productivityTotals.completedTaskSum > 0
                              ? productivityTotals.totalMinutesSum / productivityTotals.completedTaskSum
                              : 0
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {formatDuration(
                            productivityRows.filter((row) => row.staffCount > 0).length > 0
                              ? productivityRows.reduce((sum, row) => sum + row.avgMinutesPerStaff, 0)
                                / productivityRows.filter((row) => row.staffCount > 0).length
                              : 0
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                  </table>
                </div>
              </>
            ) : null}

            {showUpcomingTasks ? (
              <div className={showDailyTaskTimeSummary ? 'border-t border-gray-200 pt-4' : ''}>
                {showDailyTaskTimeSummary ? (
                  <h4 className="mb-3 text-sm font-semibold text-gray-700">Upcoming Tasks</h4>
                ) : null}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <h5 className="text-sm font-semibold text-gray-800">Today</h5>
                    <div className="mt-3 space-y-3">
                      {todayUpcomingTasks.length === 0 ? (
                        <p className="py-2 text-center text-sm text-gray-500">No tasks for today</p>
                      ) : (
                        todayUpcomingTasks.map((task) => {
                          const status = normalizeTaskStatus(task.status);
                          const dueValue = task.scheduled_date || task.dueDate || task.due_date || task.completed_at || '';
                          const dueLabel = formatUpcomingDateTime(dueValue);
                          const details = task.details || `Due: ${dueLabel}`;

                          return (
                            <div
                              key={task.id || task._id}
                              className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{task.taskId?.name}</p>
                                <p className="text-sm text-gray-500">{details}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {shouldShowStatusBadge(status) ? (
                                  <span className={`badge ${getStatusBadgeClass(status)}`}>
                                    {getStatusLabel(status)}
                                  </span>
                                ) : null}
                                {shouldShowPriorityBadge(task.priority) ? (
                                  <span className={`badge ${getPriorityBadgeClass(task.priority)}`}>
                                    {String(task.priority || 'info')}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <h5 className="text-sm font-semibold text-gray-800">Tomorrow</h5>
                    <div className="mt-3 space-y-3">
                      {tomorrowUpcomingTasks.length === 0 ? (
                        <p className="py-2 text-center text-sm text-gray-500">No tasks for tomorrow</p>
                      ) : (
                        tomorrowUpcomingTasks.map((task) => {
                          const status = normalizeTaskStatus(task.status);
                          const dueValue = task.scheduled_date || task.dueDate || task.due_date || task.completed_at || '';
                          const dueLabel = formatUpcomingDateTime(dueValue);
                          const details = task.details || `Due: ${dueLabel}`;

                          return (
                            <div
                              key={task.id || task._id}
                              className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{task.taskId?.name}</p>
                                <p className="text-sm text-gray-500">{details}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {shouldShowStatusBadge(status) ? (
                                  <span className={`badge ${getStatusBadgeClass(status)}`}>
                                    {getStatusLabel(status)}
                                  </span>
                                ) : null}
                                {shouldShowPriorityBadge(task.priority) ? (
                                  <span className={`badge ${getPriorityBadgeClass(task.priority)}`}>
                                    {String(task.priority || 'info')}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
